import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SpeedIcon from '@mui/icons-material/Speed';
import { metersApi, plantsApi, customersApi, aziendeApi } from '../../services/api';

const MeterForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    meter_number: '',
    sotto_zona: '',
    stato: 'attivo',
    intestatario: '',
    localita: '',
    citta: '',
    provincia: '',
    prezzo_litro: '',
    aliquota_iva: '',
    ultima_lettura: '',
    data_ultima_lettura: '',
    customer_id: null,
    plant_id: null,
    azienda_id: null,
    note: ''
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [selectedAzienda, setSelectedAzienda] = useState(null);
  const [aziendeOptions, setAziendeOptions] = useState([]);
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [plantInputValue, setPlantInputValue] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);
  const [customerSearchTimeout, setCustomerSearchTimeout] = useState(null);
  const [plantSearchTimeout, setPlantSearchTimeout] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Query contatore esistente
  const { data: meterData, isLoading: loadingMeter } = useQuery({
    queryKey: ['meter', id],
    queryFn: async () => {
      const response = await metersApi.getById(id);
      return response.data;
    },
    enabled: isEdit
  });

  // Query lista aziende
  useQuery({
    queryKey: ['aziende'],
    queryFn: async () => {
      const response = await aziendeApi.getAll();
      const list = response.data?.data || response.data || [];
      setAziendeOptions(list);
      return list;
    }
  });

  // Funzione di ricerca clienti con debounce
  const handleCustomerSearch = async (searchText) => {
    if (searchText.length < 2) {
      setCustomerOptions([]);
      return;
    }
    
    try {
      const response = await customersApi.getAll({ search: searchText, limit: 50 });
      const customers = response.data?.data?.customers || response.data?.data || [];
      setCustomerOptions(customers);
    } catch (error) {
      console.error('Errore ricerca clienti:', error);
      setCustomerOptions([]);
    }
  };

  // Funzione di ricerca impianti con debounce
  const handlePlantSearch = async (searchText) => {
    if (searchText.length < 2) {
      setPlantOptions([]);
      return;
    }
    
    try {
      const response = await plantsApi.getAll({ search: searchText, limit: 50 });
      const plants = response.data?.data?.plants || response.data?.data || [];
      setPlantOptions(plants);
    } catch (error) {
      console.error('Errore ricerca impianti:', error);
      setPlantOptions([]);
    }
  };

  // Popola form con dati esistenti
  useEffect(() => {
    if (meterData) {
      const meter = meterData.data || meterData;
      console.log('Meter data ricevuto per modifica:', meter);
      setFormData({
        meter_number: meter.meter_number || meter.nu_conta || meter.c_cod_cont || '',
        sotto_zona: meter.sotto_zona || meter.c_sotto_zo || '',
        stato: meter.stato || 'attivo',
        intestatario: meter.intestatario || meter.c_nome || '',
        localita: meter.localita || meter.c_localita || '',
        citta: meter.citta || meter.c_citta || '',
        provincia: meter.provincia || meter.c_provinci || '',
        prezzo_litro: meter.prezzo_litro || meter.c_prez_lit || '',
        aliquota_iva: meter.aliquota_iva || meter.c_iva || '',
        ultima_lettura: meter.ultima_lettura || meter.c_ult_lett || '',
        data_ultima_lettura: meter.data_ultima_lettura || meter.c_ult_data || '',
        customer_id: meter.customer_id,
        plant_id: meter.plant_id,
        azienda_id: meter.azienda_id || null,
        note: meter.note || ''
      });

      // Imposta cliente selezionato (minuscolo dal backend)
      if (meter.customer) {
        setSelectedCustomer(meter.customer);
        setCustomerOptions([meter.customer]); // Aggiungi alle opzioni
      }
      // Imposta impianto selezionato (minuscolo dal backend)
      if (meter.plant) {
        setSelectedPlant(meter.plant);
        setPlantOptions([meter.plant]); // Aggiungi alle opzioni
      }
      // Imposta azienda selezionata
      if (meter.azienda) {
        setSelectedAzienda(meter.azienda);
      }
    }
  }, [meterData]);

  // Debounce per ricerca clienti
  const handleCustomerInputChange = (_, value) => {
    setCustomerInputValue(value);
    
    // Cancella timeout precedente
    if (customerSearchTimeout) {
      clearTimeout(customerSearchTimeout);
    }
    
    // Imposta nuovo timeout (300ms)
    const timeout = setTimeout(() => {
      handleCustomerSearch(value);
    }, 300);
    
    setCustomerSearchTimeout(timeout);
  };

  // Debounce per ricerca impianti
  const handlePlantInputChange = (_, value) => {
    setPlantInputValue(value);
    
    // Cancella timeout precedente
    if (plantSearchTimeout) {
      clearTimeout(plantSearchTimeout);
    }
    
    // Imposta nuovo timeout (300ms)
    const timeout = setTimeout(() => {
      handlePlantSearch(value);
    }, 300);
    
    setPlantSearchTimeout(timeout);
  };

  // Mutation salvataggio
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (isEdit) {
        return metersApi.update(id, data);
      } else {
        return metersApi.create(data);
      }
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['meters']);
      queryClient.invalidateQueries(['meter', id]);
      setSnackbar({ 
        open: true, 
        message: isEdit ? 'Contatore aggiornato con successo' : 'Contatore creato con successo', 
        severity: 'success' 
      });
      setTimeout(() => {
        const meterId = response.data?.data?.id || response.data?.id || id;
        navigate(`/crm/contatori/${meterId}`);
      }, 1000);
    },
    onError: (error) => {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Errore durante il salvataggio', 
        severity: 'error' 
      });
    }
  });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleCustomerChange = (_, value) => {
    setSelectedCustomer(value);
    
    // Auto-compila intestatario con il nome del cliente
    let intestatario = '';
    if (value) {
      if (value.ragione_sociale || value.c_ragsoc) {
        intestatario = value.ragione_sociale || value.c_ragsoc;
      } else {
        const nome = value.nome || value.c_nome || '';
        const cognome = value.cognome || value.c_cognome || '';
        intestatario = `${cognome} ${nome}`.trim();
      }
    }
    
    setFormData({ 
      ...formData, 
      customer_id: value?.id || null,
      intestatario: intestatario // Auto-compila ma rimane modificabile
    });
    
    // Mantieni il cliente selezionato nelle opzioni
    if (value && !customerOptions.find(c => c.id === value.id)) {
      setCustomerOptions([...customerOptions, value]);
    }
  };

  const handlePlantChange = (_, value) => {
    setSelectedPlant(value);
    setFormData({ ...formData, plant_id: value?.id || null });
    // Mantieni l'impianto selezionato nelle opzioni
    if (value && !plantOptions.find(p => p.id === value.id)) {
      setPlantOptions([...plantOptions, value]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepara dati per invio mappando ai campi legacy del database
    const submitData = {
      ...formData,
      // Mappa campi normalizzati a campi legacy per compatibilità
      c_nome: formData.intestatario,
      c_localita: formData.localita,
      c_ult_lett: formData.ultima_lettura ? parseFloat(formData.ultima_lettura) : null,
      c_ult_data: formData.data_ultima_lettura || null,
      c_citta: formData.citta,
      c_provinci: formData.provincia,
      prezzo_litro: formData.prezzo_litro ? parseFloat(formData.prezzo_litro) : null,
      aliquota_iva: formData.aliquota_iva ? parseFloat(formData.aliquota_iva) : null,
      sotto_zona: formData.sotto_zona ? parseInt(formData.sotto_zona) : null,
      azienda_id: formData.azienda_id || null
    };

    saveMutation.mutate(submitData);
  };

  if (isEdit && loadingMeter) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 3 }}>
        <IconButton onClick={() => navigate(isEdit ? `/contatori/${id}` : '/contatori')}>
          <ArrowBackIcon />
        </IconButton>
        {!isMobile && <SpeedIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
        <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
          {isEdit ? 'Modifica Contatore' : 'Nuovo Contatore'}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={isMobile ? 2 : 3}>
          {/* Dati Contatore */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Dati Contatore</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Matricola"
                      value={formData.meter_number}
                      onChange={handleChange('meter_number')}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Progressivo (Sottozona)"
                      value={formData.sotto_zona}
                      onChange={handleChange('sotto_zona')}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Stato</InputLabel>
                      <Select
                        value={formData.stato}
                        label="Stato"
                        onChange={handleChange('stato')}
                      >
                        <MenuItem value="attivo">Attivo</MenuItem>
                        <MenuItem value="sospeso">Sospeso</MenuItem>
                        <MenuItem value="cessato">Cessato</MenuItem>
                        <MenuItem value="guasto">Guasto</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Intestatario e Ubicazione */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Intestatario e Ubicazione</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Intestatario"
                      value={formData.intestatario}
                      onChange={handleChange('intestatario')}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Località"
                      value={formData.localita}
                      onChange={handleChange('localita')}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Città"
                      value={formData.citta}
                      onChange={handleChange('citta')}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Provincia"
                      value={formData.provincia}
                      onChange={handleChange('provincia')}
                      size="small"
                      inputProps={{ maxLength: 2 }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Associazioni */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Associazioni</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={customerOptions}
                      value={selectedCustomer}
                      onChange={handleCustomerChange}
                      onInputChange={handleCustomerInputChange}
                      inputValue={customerInputValue}
                      getOptionLabel={(option) => {
                        const codice = option.cod_cli || option.c_codice || option.id;
                        const nome = option.ragione_sociale || option.nome || 
                                    option.c_ragsoc || option.c_nome || '';
                        const cognome = option.cognome || option.c_cognome || '';
                        const displayName = nome + (cognome ? ` ${cognome}` : '');
                        return `${codice} - ${displayName}`;
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Cliente Associato" 
                          size="small"
                          placeholder="Digita per cercare (min 2 caratteri)"
                          helperText="Cerca per codice, nome o ragione sociale"
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      noOptionsText={customerInputValue.length < 2 ? 'Digita almeno 2 caratteri' : 'Nessun cliente trovato'}
                      filterOptions={(x) => x} // Disabilita filtro locale, usiamo ricerca server
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={plantOptions}
                      value={selectedPlant}
                      onChange={handlePlantChange}
                      onInputChange={handlePlantInputChange}
                      inputValue={plantInputValue}
                      getOptionLabel={(option) => {
                        const codice = option.plant_code || option.delivery_code || 
                                      option.codice || option.p_codice || option.id;
                        const indirizzo = option.indirizzo || option.p_indirizzo || '';
                        const citta = option.citta || option.p_citta || '';
                        const location = [indirizzo, citta].filter(Boolean).join(', ');
                        return `${codice}${location ? ` - ${location}` : ''}`;
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Impianto Associato" 
                          size="small"
                          placeholder="Digita per cercare (min 2 caratteri)"
                          helperText="Cerca per codice, indirizzo o città"
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      noOptionsText={plantInputValue.length < 2 ? 'Digita almeno 2 caratteri' : 'Nessun impianto trovato'}
                      filterOptions={(x) => x} // Disabilita filtro locale, usiamo ricerca server
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={aziendeOptions}
                      value={selectedAzienda}
                      onChange={(_, value) => {
                        setSelectedAzienda(value);
                        setFormData({ ...formData, azienda_id: value?.id || null });
                      }}
                      getOptionLabel={(option) => option.company_name || option.nome_azienda || ''}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Azienda (override)"
                          size="small"
                          helperText={
                            selectedAzienda 
                              ? 'Azienda specifica per questo contatore' 
                              : selectedPlant?.azienda?.company_name 
                                ? `Ereditata dall'impianto: ${selectedPlant.azienda.company_name}`
                                : 'Se vuoto, eredita dall\'impianto'
                          }
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      noOptionsText="Nessuna azienda trovata"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Prezzi */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Prezzi e Tariffe</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Prezzo al Litro (€)"
                      value={formData.prezzo_litro}
                      onChange={handleChange('prezzo_litro')}
                      size="small"
                      type="number"
                      inputProps={{ step: '0.0001', min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Aliquota IVA (%)"
                      value={formData.aliquota_iva}
                      onChange={handleChange('aliquota_iva')}
                      size="small"
                      type="number"
                      inputProps={{ step: '0.01', min: 0, max: 100 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Ultima Lettura (mc)"
                      value={formData.ultima_lettura}
                      onChange={handleChange('ultima_lettura')}
                      size="small"
                      type="number"
                      inputProps={{ step: '0.01', min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Data Lettura"
                      value={formData.data_ultima_lettura}
                      onChange={handleChange('data_ultima_lettura')}
                      size="small"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Bottoni */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate(isEdit ? `/contatori/${id}` : '/contatori')}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saveMutation.isLoading}
              >
                {saveMutation.isLoading ? 'Salvataggio...' : (isEdit ? 'Salva Modifiche' : 'Crea Contatore')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MeterForm;
